import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PostCard } from "@/components/PostCard";
import { sortedPosts } from "@/data/posts";
import { siteConfig, categorySlug } from "@/config";

export const Route = createFileRoute("/category/$category")({
  loader: ({ params }) => {
    const category = siteConfig.categories.find(
      (c) => categorySlug(c) === params.category,
    );
    if (!category) throw notFound();
    const list = sortedPosts().filter((p) => p.category === category);
    return { category, list };
  },
  head: ({ loaderData, params }) => {
    const name = loaderData?.category ?? "Category";
    const title = `${name} — ${siteConfig.siteName}`;
    const desc = `Browse ${name} articles on ${siteConfig.siteName}: reviews, comparisons, and buyer's guides for CRM software.`;
    const url = `${siteConfig.domain}/category/${params.category}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CategoryPage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-3xl font-bold">Category not found</h1>
        <Link to="/" className="mt-6 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
          Back home
        </Link>
      </main>
      <Footer />
    </div>
  ),
  errorComponent: ({ error, reset }) => {
    console.error(error);
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-3xl px-4 py-16 text-center">
          <h1 className="text-xl font-semibold">Couldn't load this category.</h1>
          <button onClick={() => reset()} className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            Try again
          </button>
        </main>
        <Footer />
      </div>
    );
  },
});

function CategoryPage() {
  const { category, list } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10">
        <p className="text-sm font-medium uppercase tracking-widest text-primary">Category</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">{category}</h1>
        <p className="mt-3 text-muted-foreground">
          {list.length} article{list.length === 1 ? "" : "s"} in {category}.
        </p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p: import("@/data/posts").Post) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
